import React, { useState } from 'react';
import { MessageCircle, Clock, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { leadCommunicationAPI } from '../lib/api';

export default function SendWhatsAppModal({ isOpen, onClose, leadId, leadPhone, leadName }) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // Template picker state — shown only when the lead is outside the 24h window.
  const [needsTemplate, setNeedsTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const loadTemplates = async () => {
    try {
      const res = await leadCommunicationAPI.getWhatsAppTemplates();
      const list = res.data?.templates || [];
      setTemplates(list);
      if (list.length) setSelectedTemplate(list[0].name);
    } catch (e) {
      toast.error('Could not load approved templates');
    }
  };

  const sendSession = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error('Please enter a message'); return; }
    try {
      setLoading(true);
      const res = await leadCommunicationAPI.sendWhatsApp(leadId, { message });
      if (res.data?.requires_template) {
        setNeedsTemplate(true);
        await loadTemplates();
        toast.message('Outside the 24h window — pick an approved template to reach this lead.');
        return;
      }
      if (res.data?.success) { toast.success('WhatsApp message sent'); handleClose(); }
      else { toast.error(res.data?.message || 'WhatsApp send failed'); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp message');
    } finally {
      setLoading(false);
    }
  };

  const sendTemplate = async () => {
    if (!selectedTemplate) { toast.error('Pick a template'); return; }
    const tpl = templates.find((t) => t.name === selectedTemplate);
    try {
      setLoading(true);
      const res = await leadCommunicationAPI.sendWhatsApp(leadId, {
        template_name: selectedTemplate,
        language: tpl?.language || 'en_US',
        category: tpl?.category,
      });
      if (res.data?.success) { toast.success('Template sent via WhatsApp'); handleClose(); }
      else { toast.error(res.data?.message || 'Template send failed'); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setNeedsTemplate(false);
    setTemplates([]);
    setSelectedTemplate('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="send-whatsapp-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-500" />
            Send WhatsApp to {leadName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="to-phone">To</Label>
            <Input id="to-phone" value={leadPhone} readOnly className="bg-muted" data-testid="whatsapp-to-phone" />
          </div>

          {!needsTemplate ? (
            <form onSubmit={sendSession} className="space-y-4">
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your WhatsApp message…"
                  rows={7}
                  data-testid="whatsapp-message-input"
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="whatsapp-send-button">
                  {loading ? 'Sending…' : 'Send WhatsApp'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose} data-testid="whatsapp-cancel-button">Cancel</Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4" data-testid="whatsapp-template-picker">
              <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm">
                <Clock className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                <p className="text-amber-700 dark:text-amber-300">
                  This lead is outside the 24-hour window, so free text can’t be delivered. Choose an approved template.
                </p>
              </div>
              <div>
                <Label htmlFor="template">Approved template *</Label>
                {templates.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                    <ShieldAlert className="h-4 w-4" /> No approved templates found. Create & submit one in Settings → Integrations.
                  </div>
                ) : (
                  <select
                    id="template"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full mt-1 text-sm rounded-lg border border-border/60 bg-background px-2 py-2"
                    data-testid="whatsapp-template-select"
                  >
                    {templates.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.name}{t.category ? ` — ${t.category}` : ''}{t.language ? ` (${t.language})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="button" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={loading || !templates.length} onClick={sendTemplate} data-testid="whatsapp-send-template-button">
                  {loading ? 'Sending…' : 'Send template'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose} data-testid="whatsapp-cancel-button">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
